import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, MapPin, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BUSINESS } from "@/constants/business";

export default function Coverage() {
    const [postcode, setPostcode] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const check = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setResult(null);
        try {
            const { data } = await api.get("/coverage/check", { params: { postcode } });
            setResult(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="coverage" data-testid="coverage-section" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">
                        03 / Coverage Area
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white leading-tight">
                        Gympie locals only — for now
                    </h2>
                    <p className="mt-6 text-base text-zinc-400 leading-relaxed max-w-lg">
                        We keep it local so we can get to you fast: Gympie postcode 4570 only, same-day dispatch.
                        Just outside the boundary? Call us — we make exceptions for bigger jobs.
                    </p>
                    <ul className="mt-8 space-y-3" data-testid="coverage-districts-list">
                        {["Gympie, Southside & Mary Valley — 4570"].map((d) => (
                            <li key={d} className="flex items-center gap-3 text-sm text-zinc-300">
                                <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
                                <span className="font-mono text-xs uppercase tracking-[0.15em]">{d}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                    className="rounded-2xl border border-[#22262F] bg-[#121418] p-8 sm:p-10"
                    data-testid="postcode-checker-card"
                >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] text-zinc-500 mb-6">
                        Dispatch Radar — Postcode Verification
                    </div>
                    <form onSubmit={check} className="flex gap-3">
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={postcode}
                            onChange={(e) => setPostcode(e.target.value.replace(/\D/g, ""))}
                            placeholder="Enter postcode"
                            data-testid="postcode-check-input"
                            className="flex-1 bg-[#0B0C0E] border border-[#383E4C] rounded-xl px-5 py-4 text-white font-mono text-sm placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors duration-200"
                        />
                        <motion.button
                            type="submit"
                            disabled={loading || postcode.length !== 4}
                            data-testid="postcode-check-submit-button"
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            className="bg-amber-500 disabled:opacity-40 text-[#0B0C0E] font-bold uppercase tracking-widest text-xs rounded-xl px-6 py-4 hover:bg-amber-400 transition-colors duration-200"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}
                        </motion.button>
                    </form>

                    {error && (
                        <div className="mt-6 text-sm text-red-400 font-mono" data-testid="postcode-check-error">
                            {error}
                        </div>
                    )}
                    {result && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className={`mt-6 rounded-xl border p-5 flex items-start gap-4 ${
                                result.covered
                                    ? "border-amber-500/40 bg-amber-500/10"
                                    : "border-red-500/40 bg-red-500/10"
                            }`}
                            data-testid="postcode-check-result"
                        >
                            {result.covered ? (
                                <CheckCircle2 className="w-6 h-6 text-amber-400 shrink-0" />
                            ) : (
                                <XCircle className="w-6 h-6 text-red-400 shrink-0" />
                            )}
                            <div>
                                <div className="font-display text-lg font-bold uppercase text-white">
                                    {result.covered ? `Covered — ${result.district}` : "Outside our zone"}
                                </div>
                                <div className="text-xs font-mono text-zinc-400 mt-1 uppercase tracking-wider">
                                    {result.eta}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <div className="mt-8 pt-6 border-t border-[#22262F] flex flex-wrap items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600">
                        <span>{BUSINESS.location}</span>
                        <span>{BUSINESS.hours}</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
